/**
 * Çerçeve Atölyesi — Glowy ekibinin Claude'la marka çerçevesi ürettiği iç ekran.
 * Claude kod yazıp kendi kabında çalıştırıyor; galeriyi yayınlıyor, feed bağlama
 * talebi açıyor. Müşteri bu ekranı görmez, yalnızca galeri linkini görür.
 * Kilit backend'de (/api/cerceve-atolyesi, yalnız @heyglowy.com).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import {
  Plus, Send, Square, Paperclip, ExternalLink, Copy, Check, X, ChevronRight, ChevronDown,
  Loader2, Terminal, FileText, Globe, Image as ImageIcon, Upload, AlertTriangle, Frame, Inbox,
} from 'lucide-react';

const BASE = 'https://api.glowytics.com/api/cerceve-atolyesi';

// Her istek panelde seçili markayla gider; backend çalışmaları bu markaya kilitler.
async function api(brandId, yol, secenek = {}) {
  const r = await fetch(`${BASE}${yol}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' },
    ...secenek,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) {
    const e = new Error(j.error || `HTTP ${r.status}`);
    e.status = r.status;
    throw e;
  }
  return j;
}

const zamanFmt = (z) => (z ? new Date(z).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const kisaEposta = (e) => String(e || '').split('@')[0];

const DURUM = {
  running: { ad: 'Çalışıyor', sinif: 'bg-amber-50 text-amber-700 ring-amber-200' },
  idle: { ad: 'Sıra sende', sinif: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  rescheduling: { ad: 'Yeniden deniyor', sinif: 'bg-amber-50 text-amber-700 ring-amber-200' },
  terminated: { ad: 'Kapandı', sinif: 'bg-gray-100 text-gray-500 ring-gray-200' },
};

const ARAC_ADI = {
  bash: 'Komut', read: 'Okudu', write: 'Yazdı', edit: 'Düzenledi', glob: 'Dosya aradı', grep: 'İçerik aradı',
  web_fetch: 'Sayfa çekti', web_search: 'Web araması', galeri_yayinla: 'Galeri yayınladı',
  feed_baglama_talebi: 'Feed talebi açtı', marka_kaynaklari: 'Kaynaklara baktı',
};
const aracIkonu = (ad) => ({
  bash: Terminal, read: FileText, write: FileText, edit: FileText, web_fetch: Globe, web_search: Globe,
  galeri_yayinla: ImageIcon, feed_baglama_talebi: Upload,
}[ad] || ChevronRight);

// Ardışık araç adımlarını tek katlanır blokta topla.
function gruplandir(olaylar) {
  const out = [];
  for (const o of olaylar) {
    if (o.tur === 'arac') {
      const son = out[out.length - 1];
      if (son && son.tur === 'adimlar') son.adimlar.push(o);
      else out.push({ tur: 'adimlar', id: `g-${o.id}`, adimlar: [o] });
    } else out.push(o);
  }
  return out;
}

function Adimlar({ grup, acikBaslangic }) {
  const [acik, setAcik] = useState(acikBaslangic);
  useEffect(() => { if (acikBaslangic) setAcik(true); }, [acikBaslangic]);
  const calisan = grup.adimlar.some((a) => a.durum === 'calisiyor');
  const hatali = grup.adimlar.filter((a) => a.durum === 'hata').length;
  const onemli = grup.adimlar.filter((a) => a.ozel);
  return (
    <div className="my-2">
      <button type="button" onClick={() => setAcik(!acik)}
        className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800">
        {acik ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {calisan && <Loader2 size={12} className="animate-spin text-amber-500" />}
        <span>{grup.adimlar.length} adım</span>
        {hatali > 0 && <span className="text-rose-500">· {hatali} hata (Claude düzeltiyor)</span>}
        {!acik && onemli.map((a) => (
          <span key={a.id} className="ml-1 rounded-full bg-violet-50 px-2 py-0.5 text-violet-700">{ARAC_ADI[a.ad] || a.ad}</span>
        ))}
      </button>
      {acik && (
        <ul className="mt-1.5 space-y-1 border-l-2 border-gray-100 pl-3">
          {grup.adimlar.map((a) => <Adim key={a.id} a={a} />)}
        </ul>
      )}
    </div>
  );
}

function Adim({ a }) {
  const [detay, setDetay] = useState(false);
  const Ikon = aracIkonu(a.ad);
  const renk = a.durum === 'hata' ? 'text-rose-500' : a.durum === 'calisiyor' ? 'text-amber-500' : 'text-gray-400';
  return (
    <li className="text-xs">
      <button type="button" onClick={() => setDetay(!detay)} className="flex w-full items-start gap-2 text-left">
        {a.durum === 'calisiyor' ? <Loader2 size={13} className={`mt-0.5 shrink-0 animate-spin ${renk}`} /> : <Ikon size={13} className={`mt-0.5 shrink-0 ${renk}`} />}
        <span className="shrink-0 font-medium text-gray-700">{ARAC_ADI[a.ad] || a.ad}</span>
        <span className="min-w-0 flex-1 truncate text-gray-500">{a.ozet}</span>
        {a.gorsel && <ImageIcon size={12} className="mt-0.5 shrink-0 text-violet-400" title="Görsele baktı" />}
      </button>
      {detay && (
        <div className="mt-1 space-y-1 rounded-lg bg-gray-50 p-2 text-[11px] leading-relaxed text-gray-600">
          <div className="whitespace-pre-wrap break-all">{a.ozet}</div>
          {a.sonuc && <div className="whitespace-pre-wrap break-all border-t border-gray-200 pt-1 text-gray-500">{a.sonuc}</div>}
        </div>
      )}
    </li>
  );
}

function Mesaj({ o }) {
  if (o.tur === 'kullanici') {
    const m = o.metin.match(/^\[([^\]]+)\]\s*([\s\S]*)$/);
    return (
      <div className="my-3 flex justify-end">
        <div className="max-w-[80%] rounded-2xl rounded-br-md bg-gray-900 px-4 py-2.5 text-sm text-white">
          {m && <div className="mb-0.5 text-[11px] text-gray-400">{kisaEposta(m[1])}</div>}
          <div className="whitespace-pre-wrap break-words">{m ? m[2] : o.metin}</div>
        </div>
      </div>
    );
  }
  if (o.tur === 'claude') {
    return (
      <div className="prose prose-sm my-3 max-w-none text-gray-800 prose-a:text-violet-700 prose-pre:bg-gray-50 prose-pre:text-gray-700">
        <ReactMarkdown remarkPlugins={[remarkGfm]}
          components={{ a: ({ node, ...p }) => <a {...p} target="_blank" rel="noreferrer" /> }}>
          {o.metin}
        </ReactMarkdown>
      </div>
    );
  }
  if (o.tur === 'hata') {
    return <div className="my-2 flex items-center gap-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700"><AlertTriangle size={14} />{o.metin}</div>;
  }
  return <div className="my-2 text-center text-xs text-gray-400">{o.metin}</div>;
}

function KopyalaDugme({ metin }) {
  const [tamam, setTamam] = useState(false);
  return (
    <button type="button" title="Linki kopyala"
      onClick={() => { navigator.clipboard.writeText(metin); setTamam(true); setTimeout(() => setTamam(false), 1500); }}
      className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
      {tamam ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
    </button>
  );
}

function TalepKarti({ t, kararVer }) {
  const [not, setNot] = useState('');
  const [mesgul, setMesgul] = useState(false);
  const cerceveler = Array.isArray(t.cerceveler) ? t.cerceveler : [];
  const urunSayisi = cerceveler.reduce((n, c) => n + (c.urun_idleri?.length || 0), 0);
  const ver = async (karar) => {
    setMesgul(true);
    try { await kararVer(t.id, karar, not); } finally { setMesgul(false); }
  };
  const etiket = { bekliyor: 'Onay bekliyor', onaylandi: 'Onaylandı', reddedildi: 'Reddedildi' }[t.durum] || t.durum;
  const renk = { bekliyor: 'text-amber-700 bg-amber-50', onaylandi: 'text-emerald-700 bg-emerald-50', reddedildi: 'text-gray-500 bg-gray-100' }[t.durum];
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-gray-900" title={t.feed}>{t.feed}</div>
          <div className="text-xs text-gray-500">{cerceveler.length} çerçeve · {urunSayisi} ürün{t.calisma ? ` · ${t.calisma}` : ''}</div>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${renk}`}>{etiket}</span>
      </div>
      {t.aciklama && <p className="mt-2 text-xs leading-relaxed text-gray-600">{t.aciklama}</p>}
      <div className="mt-2 space-y-2">
        {cerceveler.map((c) => (
          <div key={c.ad}>
            <div className="mb-1 text-[11px] font-medium text-gray-700">{c.ad}</div>
            <div className="flex gap-1.5">
              {[c.onizleme?.kare, c.onizleme?.dikey, ...(c.onizleme?.ornekler || [])].filter(Boolean).slice(0, 4).map((d) => (
                <a key={d} href={`${t.onizleme_url}/${d}`} target="_blank" rel="noreferrer"
                  className="block h-14 w-14 overflow-hidden rounded-md bg-[conic-gradient(#eee_25%,#fff_0_50%,#eee_0_75%,#fff_0)] bg-[length:10px_10px] ring-1 ring-gray-200">
                  <img src={`${t.onizleme_url}/${d}`} alt="" className="h-full w-full object-contain" />
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
      {t.durum === 'bekliyor' ? (
        <div className="mt-3 space-y-2">
          <input value={not} onChange={(e) => setNot(e.target.value)} placeholder="Not (reddederken Claude'a iletilir)"
            className="w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs focus:border-violet-400 focus:outline-none" />
          <div className="flex gap-2">
            <button type="button" disabled={mesgul} onClick={() => ver('onay')}
              className="flex-1 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-700 disabled:opacity-50">Onayla</button>
            <button type="button" disabled={mesgul} onClick={() => ver('red')}
              className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Reddet</button>
          </div>
          <p className="text-[11px] leading-snug text-gray-400">Onaylanan talep şimdilik teknik ekibe düşer; canlı feed'e otomatik uygulama sonraki aşamada.</p>
        </div>
      ) : (
        <div className="mt-2 text-[11px] text-gray-400">{kisaEposta(t.karar_veren)} · {zamanFmt(t.karar_at)}{t.karar_notu ? ` · ${t.karar_notu}` : ''}</div>
      )}
    </div>
  );
}

function YeniCalisma({ kapat, olustur, marka }) {
  const [baslik, setBaslik] = useState('');
  const [mesaj, setMesaj] = useState('');
  const [mesgul, setMesgul] = useState(false);
  const gonder = async (e) => {
    e.preventDefault();
    if (!baslik.trim()) return;
    setMesgul(true);
    try { await olustur(baslik.trim(), mesaj.trim()); } finally { setMesgul(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={kapat}>
      <form onSubmit={gonder} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Yeni çerçeve çalışması · {marka}</h2>
          <button type="button" onClick={kapat} className="rounded-md p-1 text-gray-400 hover:bg-gray-100"><X size={16} /></button>
        </div>
        <label className="mb-1 block text-xs font-medium text-gray-600">Çalışmanın adı</label>
        <input autoFocus value={baslik} onChange={(e) => setBaslik(e.target.value)} placeholder="Hupalupa · Learning Resources"
          className="mb-4 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none" />
        <label className="mb-1 block text-xs font-medium text-gray-600">Claude'a ilk mesaj</label>
        <textarea value={mesaj} onChange={(e) => setMesaj(e.target.value)} rows={5}
          placeholder={'Learning Resources Hupalupa Store için çerçeve çalışmamız gerekiyor.\nMarka rengi ve maskotlar öne çıksın, 4 seçenek yeterli.'}
          className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none" />
        <p className="mt-2 text-xs text-gray-400">Claude kaynağı bulur, ürünleri ayırır, çerçeveleri üretip kendi kontrol eder ve müşteriye gidecek galeriyi yayınlar. Bir çalışma birkaç dakika sürer.</p>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={kapat} className="rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100">Vazgeç</button>
          <button type="submit" disabled={mesgul || !baslik.trim()}
            className="flex items-center gap-1.5 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 disabled:opacity-50">
            {mesgul && <Loader2 size={14} className="animate-spin" />} Başlat
          </button>
        </div>
      </form>
    </div>
  );
}

export default function CerceveAtolyesi() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || null;
  const anahtar = `atolye:secili:${brandId}`;
  const [erisimYok, setErisimYok] = useState(false);
  const [oturumlar, setOturumlar] = useState([]);
  const [secili, setSecili] = useState(null);
  const [veri, setVeri] = useState(null);
  const [yeni, setYeni] = useState(false);
  const [metin, setMetin] = useState('');
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [bekleyenGonderim, setBekleyenGonderim] = useState(false);
  const [sekme, setSekme] = useState('calismalar');
  const [tumTalepler, setTumTalepler] = useState([]);
  const altRef = useRef(null);
  const dosyaRef = useRef(null);
  const sonOlaySayisi = useRef(0);

  const listeYukle = useCallback(async () => {
    if (!brandId) return;
    try {
      const [o, t] = await Promise.all([api(brandId, '/oturumlar'), api(brandId, '/talepler')]);
      setOturumlar(o.oturumlar); setTumTalepler(t.talepler);
    } catch (e) {
      if (e.status === 404 || e.status === 401) setErisimYok(true);
      else if (e.status === 403) { setOturumlar([]); setTumTalepler([]); toast.error(e.message, { id: 'marka' }); }
      else toast.error(e.message);
    }
  }, [brandId]);

  const oturumYukle = useCallback(async (sid) => {
    if (!sid || !brandId) return;
    try {
      const j = await api(brandId, `/oturumlar/${sid}`);
      setVeri((v) => (v && v.oturum.session_id !== sid ? v : j));
      if (j.oturum.durum === 'running') setBekleyenGonderim(false);
    } catch (e) {
      if (e.status === 404) { setSecili(null); setVeri(null); }
      else if (e.status === 402) toast.error(e.message, { id: 'kredi' });
    }
  }, [brandId]);

  // Marka değişince: o markanın listesi + o markada en son açık çalışma (yoksa boş).
  useEffect(() => {
    setOturumlar([]); setTumTalepler([]);
    let son = null;
    try { son = localStorage.getItem(anahtar); } catch { /* yok */ }
    setSecili(son);
    listeYukle();
  }, [listeYukle, anahtar]);
  useEffect(() => {
    try { if (secili) localStorage.setItem(anahtar, secili); } catch { /* yok */ }
    setVeri(null); sonOlaySayisi.current = 0;
    oturumYukle(secili);
  }, [secili, oturumYukle]);

  const calisiyor = veri?.oturum?.durum === 'running' || bekleyenGonderim;
  useEffect(() => {
    if (!secili) return undefined;
    const t = setInterval(() => { if (!document.hidden) oturumYukle(secili); }, calisiyor ? 2500 : 12000);
    return () => clearInterval(t);
  }, [secili, calisiyor, oturumYukle]);
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) listeYukle(); }, 30000);
    return () => clearInterval(t);
  }, [listeYukle]);

  const gruplar = useMemo(() => gruplandir(veri?.olaylar || []), [veri]);
  useEffect(() => {
    const n = veri?.olaylar?.length || 0;
    if (n > sonOlaySayisi.current) altRef.current?.scrollIntoView({ behavior: sonOlaySayisi.current ? 'smooth' : 'auto' });
    sonOlaySayisi.current = n;
  }, [veri]);

  const olustur = async (baslik, mesaj) => {
    try {
      const j = await api(brandId, '/oturumlar', { method: 'POST', body: JSON.stringify({ baslik, mesaj }) });
      setYeni(false); setSekme('calismalar');
      if (mesaj) setBekleyenGonderim(true);
      setSecili(j.session_id);
      listeYukle();
    } catch (e) { toast.error(e.message); }
  };

  const gonder = async () => {
    const m = metin.trim();
    if (!m || !secili || gonderiliyor) return;
    setGonderiliyor(true);
    try {
      await api(brandId, `/oturumlar/${secili}/mesaj`, { method: 'POST', body: JSON.stringify({ metin: m }) });
      setMetin(''); setBekleyenGonderim(true);
      setTimeout(() => oturumYukle(secili), 800);
    } catch (e) { toast.error(e.message); } finally { setGonderiliyor(false); }
  };

  const durdur = async () => {
    try { await api(brandId, `/oturumlar/${secili}/durdur`, { method: 'POST' }); setBekleyenGonderim(false); toast('Durduruldu'); } catch (e) { toast.error(e.message); }
  };

  const dosyaSec = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error('15 MB üstü'); return; }
    const veriUrl = await new Promise((ok, red) => { const r = new FileReader(); r.onload = () => ok(r.result); r.onerror = red; r.readAsDataURL(f); });
    try {
      const j = await api(brandId, '/yukle', { method: 'POST', body: JSON.stringify({ veri: veriUrl }) });
      setMetin((m) => `${m}${m && !m.endsWith('\n') ? '\n' : ''}Ek (${f.name}): ${j.url}\n`);
    } catch (err) { toast.error(err.message); }
  };

  const kararVer = async (id, karar, not) => {
    try {
      await api(brandId, `/talepler/${id}/karar`, { method: 'POST', body: JSON.stringify({ karar, not }) });
      toast.success(karar === 'onay' ? 'Onaylandı' : 'Reddedildi');
      listeYukle(); oturumYukle(secili);
    } catch (e) { toast.error(e.message); }
  };

  if (!brandId) {
    return <div className="flex h-[70vh] items-center justify-center text-sm text-gray-500">Önce panelden bir marka seç; çalışmalar seçili markaya bağlıdır.</div>;
  }
  if (erisimYok) {
    return <div className="flex h-[70vh] items-center justify-center text-sm text-gray-500">Bu sayfa yalnızca Glowy ekibine açık.</div>;
  }

  const durum = DURUM[veri?.oturum?.durum] || null;
  const bekleyenSayisi = tumTalepler.filter((t) => t.durum === 'bekliyor').length;

  return (
    <div className="flex h-[calc(100vh-64px)] min-h-[560px] overflow-hidden bg-gray-50">
      {/* Sol: çalışmalar / talepler */}
      <aside className="flex w-64 shrink-0 flex-col border-r border-gray-200 bg-white max-md:hidden">
        <div className="border-b border-gray-100 p-3">
          <div className="mb-3 flex items-center gap-2 px-1">
            <Frame size={18} className="text-violet-600" />
            <span className="text-sm font-semibold text-gray-900">Çerçeve Atölyesi</span>
          </div>
          <div className="mb-3 truncate px-1 text-xs text-gray-500">Marka: <span className="font-medium text-gray-800">{selectedBrand?.name}</span></div>
          <button type="button" onClick={() => setYeni(true)}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-700">
            <Plus size={15} /> Yeni çalışma
          </button>
          <div className="mt-3 grid grid-cols-2 gap-1 rounded-lg bg-gray-100 p-0.5 text-xs">
            {[['calismalar', 'Çalışmalar'], ['talepler', `Feed talepleri${bekleyenSayisi ? ` (${bekleyenSayisi})` : ''}`]].map(([k, ad]) => (
              <button key={k} type="button" onClick={() => setSekme(k)}
                className={`rounded-md px-2 py-1 ${sekme === k ? 'bg-white font-medium text-gray-900 shadow-sm' : 'text-gray-500'}`}>{ad}</button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {sekme === 'calismalar' ? (
            oturumlar.length === 0 ? <p className="p-3 text-xs text-gray-400">Henüz çalışma yok.</p>
              : oturumlar.map((o) => (
                <button key={o.session_id} type="button" onClick={() => setSecili(o.session_id)}
                  className={`mb-0.5 w-full rounded-lg px-3 py-2 text-left ${secili === o.session_id ? 'bg-violet-50' : 'hover:bg-gray-50'}`}>
                  <div className="truncate text-sm text-gray-900">{o.baslik}</div>
                  <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
                    <span>{kisaEposta(o.olusturan)}</span><span>·</span><span>{zamanFmt(o.updated_at)}</span>
                    {o.bekleyen_talep > 0 && <span className="ml-auto rounded-full bg-amber-100 px-1.5 text-amber-700">{o.bekleyen_talep}</span>}
                  </div>
                </button>
              ))
          ) : (
            <div className="space-y-2 p-1">
              {tumTalepler.length === 0 ? <p className="p-2 text-xs text-gray-400">Talep yok.</p>
                : tumTalepler.map((t) => <TalepKarti key={t.id} t={t} kararVer={kararVer} />)}
            </div>
          )}
        </div>
      </aside>

      {/* Orta: sohbet */}
      <main className="flex min-w-0 flex-1 flex-col">
        {!secili ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
            <Frame size={36} className="text-violet-300" />
            <div className="text-base font-medium text-gray-800">Bir markaya çerçeve çalışması başlat</div>
            <p className="max-w-md text-sm text-gray-500">Claude feed'i çeker, markanın ürünlerini ayırır, logoları bulur, çerçeve seçeneklerini üretip kendisi kontrol eder ve müşteriye gönderilecek galeriyi yayınlar. Seçim gelince feed'e bağlama talebi açar.</p>
            <button type="button" onClick={() => setYeni(true)} className="mt-2 flex items-center gap-1.5 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700">
              <Plus size={15} /> Yeni çalışma
            </button>
          </div>
        ) : (
          <>
            <header className="flex items-center gap-3 border-b border-gray-200 bg-white px-5 py-3">
              <button type="button" onClick={() => setSecili(null)} className="text-xs text-violet-700 md:hidden">← Liste</button>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-gray-900">{veri?.oturum?.baslik || '…'}</div>
                <div className="text-[11px] text-gray-400">{veri ? `${kisaEposta(veri.oturum.olusturan)} başlattı · ${zamanFmt(veri.oturum.created_at)}` : ''}</div>
              </div>
              {durum && <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${durum.sinif}`}>{durum.ad}</span>}
              {calisiyor && (
                <button type="button" onClick={durdur} className="flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50">
                  <Square size={11} /> Durdur
                </button>
              )}
            </header>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <div className="mx-auto max-w-3xl">
                {!veri && <div className="flex justify-center py-10"><Loader2 className="animate-spin text-gray-300" /></div>}
                {gruplar.map((g, i) => (g.tur === 'adimlar'
                  ? <Adimlar key={g.id} grup={g} acikBaslangic={calisiyor && i === gruplar.length - 1} />
                  : <Mesaj key={g.id} o={g} />))}
                {calisiyor && (
                  <div className="my-3 flex items-center gap-2 text-xs text-gray-400"><Loader2 size={13} className="animate-spin" /> Claude çalışıyor…</div>
                )}
                <div ref={altRef} />
              </div>
            </div>
            <div className="border-t border-gray-200 bg-white p-3">
              <div className="mx-auto flex max-w-3xl items-end gap-2 rounded-2xl border border-gray-200 bg-white p-2 focus-within:border-violet-300">
                <input ref={dosyaRef} type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="hidden" onChange={dosyaSec} />
                <button type="button" title="Görsel / brief ekle" onClick={() => dosyaRef.current?.click()}
                  className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"><Paperclip size={16} /></button>
                <textarea value={metin} onChange={(e) => setMetin(e.target.value)} rows={Math.min(6, Math.max(1, metin.split('\n').length))}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); gonder(); } }}
                  placeholder={calisiyor ? 'Claude çalışırken de yazabilirsin, sıraya alınır…' : 'Mesaj yaz (Enter gönderir, Shift+Enter yeni satır)'}
                  className="max-h-48 flex-1 resize-none bg-transparent py-1.5 text-sm focus:outline-none" />
                <button type="button" onClick={gonder} disabled={!metin.trim() || gonderiliyor}
                  className="rounded-lg bg-gray-900 p-2 text-white hover:bg-gray-700 disabled:opacity-30">
                  {gonderiliyor ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </div>
            </div>
          </>
        )}
      </main>

      {/* Sağ: galeriler + bu çalışmanın talepleri */}
      {secili && veri && (
        <aside className="w-80 shrink-0 overflow-y-auto border-l border-gray-200 bg-white p-4 max-lg:hidden">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Müşteri galerileri</h3>
          {veri.galeriler.length === 0 ? <p className="mb-5 text-xs text-gray-400">Claude galeriyi yayınlayınca linki burada.</p> : (
            <div className="mb-5 space-y-2">
              {veri.galeriler.map((g) => (
                <div key={g.token} className="rounded-xl border border-gray-200 p-3">
                  <div className="text-sm font-medium text-gray-900">{g.baslik}</div>
                  <div className="mb-2 text-[11px] text-gray-400">{g.gorsel_sayisi} görsel · {zamanFmt(g.updated_at)}</div>
                  <div className="flex items-center gap-1">
                    <a href={g.url} target="_blank" rel="noreferrer"
                      className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-gray-900 px-2 py-1.5 text-xs font-medium text-white hover:bg-gray-700">
                      <ExternalLink size={12} /> Aç
                    </a>
                    <KopyalaDugme metin={g.url} />
                  </div>
                </div>
              ))}
            </div>
          )}
          <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500"><Inbox size={13} /> Feed talepleri</h3>
          {veri.talepler.length === 0 ? <p className="text-xs text-gray-400">Seçim gelince Claude'dan feed'e bağlamasını iste; talep burada onaya düşer.</p>
            : <div className="space-y-2">{veri.talepler.map((t) => <TalepKarti key={t.id} t={t} kararVer={kararVer} />)}</div>}
        </aside>
      )}

      {yeni && <YeniCalisma kapat={() => setYeni(false)} olustur={olustur} marka={selectedBrand?.name} />}
    </div>
  );
}
